<template>
  <div class="card">
    <h4>Liste de vos transactions</h4>
    <p>
      Retrouvez tout l'historique de vos transactions ainsi que les factures
      associées
    </p>
    <Table :data="currentCompany.transactions" :fields="state.fields">
      <template #creation_time="{ data }">
        <div
          :style="{ width: 'fit-content' }"
          v-text="getDate(data.creation_time, 'D MMM YYYY')"
          v-tooltip="{
            content: getDate(data.creation_time, 'D MMM YYYY à HH[h]mm'),
            placement: 'top',
          }"
        />
      </template>
      <template #status="statusProps">
        <div
          class="tag"
          :class="{
            green: statusProps.data.paid_time,
            red: statusProps.data.canceled_time,
            'grey-light':
              !statusProps.data.paid_time && !statusProps.data.canceled_time,
          }"
        >
          <span v-if="statusProps.data.paid_time">Payé</span>
          <span v-else-if="statusProps.data.canceled_time"> Annulé </span>
          <span v-else>En cours</span>
        </div>
      </template>
      <template #actions="actionsProps">
        <Button
          label="Télécharger la facture"
          class="icon icon-left outline secondary"
          icon="bi-upload"
          :disabled="!actionsProps.row.paid_time"
          :loading="state.btnIsLoading === actionsProps.row.transaction_id"
          @click="getInvoice(actionsProps.row.transaction_id)"
        />
      </template>
      <template #empty>
        <Empty
          message="<b>Aucune transaction</b> n’a été enregistrée pour le moment."
          :dark="isDark"
          icon="folder"
        />
      </template>
    </Table>
  </div>
</template>

<script setup>
import { reactive } from "vue";
import { useCompanyStore } from "@/stores/company.js";
import { useRoute } from "vue-router";
import { useDark } from "@vueuse/core";
import { useToast } from "vue-toastification";
import { getDate, dateIsBefore } from "@/composables/utils.js";
import Empty from "@/components/table/Empty.vue";

const isDark = useDark();

const route = useRoute();

const toast = useToast();

const { currentCompany, getCompanyTransactions, getCompanyTransactionInvoice } =
  useCompanyStore();

if (!currentCompany.transactions) {
  await getCompanyTransactions(route.params.companyId);
}

const state = reactive({
  btnIsLoading: false,
  fields: [
    {
      id: "name",
      label: "Nom de la transaction",
      sort(a, b, dir) {
        if (dir === "ASC") return a.localeCompare(b);
        return b.localeCompare(a);
      },
    },
    {
      id: "amount_total",
      label: "Prix de la transaction",
      defaultSortDir: "DESC",
      template(value) {
        return `${value}€`;
      },
      sort(a, b, dir) {
        if (dir === "ASC") return a - b;
        return b - a;
      },
    },
    {
      id: "creation_time",
      label: "Date de la transaction",
      defaultSortDir: "DESC",
      sort(a, b, dir) {
        if (dir === "ASC") {
          return dateIsBefore(b, a) ? 1 : -1;
        } else {
          return dateIsBefore(a, b) ? 1 : -1;
        }
      },
    },
    {
      id: "status",
      label: "Statut",
      defaultSortDir: "DESC",
      multipleSort: true,
      sort(a, b, dir) {
        if (dir === "ASC") {
          if (b.paid_time && !a.paid_time) return -1;
          if (!b.paid_time && a.paid_time) return 1;

          if (b.canceled_time && !a.canceled_time) return -1;
          if (!b.canceled_time && a.canceled_time) return 1;

          return dateIsBefore(b.creation_time, a.creation_time) ? 1 : -1;
        }

        if (a.paid_time && !b.paid_time) return -1;
        if (!a.paid_time && b.paid_time) return 1;

        if (a.canceled_time && !b.canceled_time) return -1;
        if (!a.canceled_time && b.canceled_time) return 1;

        return dateIsBefore(a.creation_time, b.creation_time) ? 1 : -1;
      },
    },
  ],
});

async function getInvoice(transaction_id) {
  state.btnIsLoading = transaction_id;

  const { invoice_url } = await getCompanyTransactionInvoice(
    route.params.companyId,
    transaction_id
  );

  if (invoice_url) {
    window.open(invoice_url, "_blank");
    state.btnIsLoading = false;
  } else {
    toast.error(
      "Un problème est survenue lors de la récupération de la facture."
    );
  }
}
</script>
<style lang="scss" scoped>
.card {
  flex: 1;
}
</style>
